<template>
  <div class="kf-tracker">
    <PageHeader subtitle="Every borrower the agent has messaged or looked at. Open one to see each day's message and why." />

    <!-- A shadow plan or a send dry run reaches nobody; say so once. -->
    <div v-if="summary.test_mode" class="kf-test">
      <LucideIcon name="info" :size="15" />
      Test mode: messages are not really sent to borrowers right now.
    </div>

    <!-- Borrowers no approved message fitted, and what template would have. -->
    <AgentTemplateGaps :reload-key="refreshCount" />

    <FilterBar
      :search.sync="search"
      :show-date="false"
      :show-reset="false"
      :result-count="error ? null : totalCount"
      result-label="borrowers"
      search-placeholder="Search user ID, mobile or name"
      @change="reload"
    >
      <template #actions>
        <v-btn small text class="text-none" :disabled="!!loading" @click="refresh">
          <LucideIcon name="refresh-cw" :size="15" class="mr-1" :spin="!!loading" />
          Refresh
        </v-btn>
      </template>
    </FilterBar>

    <DataTable
      class="mt-3"
      :headers="headers"
      :items="items"
      :loading="loading"
      :error="error"
      :page="page"
      :total-pages="totalPages"
      :total-count="totalCount"
      :page-length="pageLength"
      item-key="user_id"
      show-expand
      :expanded.sync="expanded"
      empty-title="No borrowers yet"
      empty-message="The agent has not planned a message for or looked at anyone yet, or nobody matches the search."
      empty-icon="users"
      @page-change="onPage"
      @retry="load"
      @row-click="toggleRow"
    >
      <template #[`item.user_id`]="{ item }">
        <div class="kf-user">
          <span v-if="item.customer_name">{{ item.customer_name }}</span>
          <span v-else class="kf-muted">No name</span>
          <div class="kf-detail">{{ item.mobile || "-" }} · ID {{ item.user_id }}</div>
        </div>
      </template>

      <!-- Their most recent day in one sentence: got a message (or will), or why not. -->
      <template #[`item.latest_line`]="{ item }">
        <div class="kf-line">{{ item.latest_line || "-" }}</div>
      </template>

      <template #[`item.days`]="{ item }">
        <div class="kf-counts">
          <span v-if="item.sent"><strong>{{ fmt.number(item.sent) }}</strong> sent</span>
          <span v-if="item.waiting"><strong>{{ fmt.number(item.waiting) }}</strong> waiting</span>
          <span v-if="item.no_message">
            <strong>{{ fmt.number(item.no_message) }}</strong> no message
          </span>
        </div>
      </template>

      <template #[`item.last_day`]="{ item }">
        <span class="kf-nowrap">{{ dayIst(item.last_day) }}</span>
      </template>

      <!-- Their days, loaded when the row opens. -->
      <template #expanded-item="{ headers: cols, item }">
        <td :colspan="cols.length" class="kf-expanded">
          <AgentUserDays
            :key="`${item.user_id}:${loadCount}`"
            :user-id="item.user_id"
            :mark-rehearsals="!summary.test_mode"
          />
        </td>
      </template>
    </DataTable>
  </div>
</template>

<script>
import AgentTemplateGaps from "@/components/karrofin/AgentTemplateGaps.vue";
import AgentUserDays from "@/components/karrofin/AgentUserDays.vue";
import DataTable from "@/components/karrofin/DataTable.vue";
import FilterBar from "@/components/karrofin/FilterBar.vue";
import PageHeader from "@/components/karrofin/PageHeader.vue";
import api from "@/app-js/karrofin/api";
import fmt from "@/app-js/karrofin/format";
import { dayIst } from "./common";

export default {
  name: "KarroFinAgentTracker",
  components: { AgentTemplateGaps, AgentUserDays, DataTable, FilterBar, PageHeader },
  data() {
    return {
      fmt,
      loading: false,
      error: null,
      items: [],
      expanded: [],
      page: 1,
      pageLength: 20,
      totalPages: 1,
      totalCount: null,
      search: "",
      summary: {},
      ticket: 0,
      // Bumped on every load, so an open row fetches its days again.
      loadCount: 0,
      // Bumped by Refresh only: the missing-templates panel loads again.
      refreshCount: 0,
      // Fixed widths (with the fixed table layout in the styles) so opening a
      // borrower's days never moves the columns; Latest takes the rest.
      headers: [
        { text: "", value: "data-table-expand", width: 56 },
        { text: "User", value: "user_id", width: 240 },
        { text: "Latest", value: "latest_line" },
        { text: "Messages", value: "days", width: 200 },
        { text: "Last activity", value: "last_day", width: 160 },
      ],
    };
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      const ticket = ++this.ticket;
      this.loading = true;
      this.error = null;
      api
        .getGrowthUsers({
          page_number: this.page,
          page_length: this.pageLength,
          search: (this.search || "").trim() || undefined,
        })
        .then((data) => {
          if (ticket !== this.ticket) return;
          this.loadCount += 1;
          this.items = data.results;
          this.totalPages = data.total_page_count;
          this.totalCount = data.total_count;
          this.page = data.page_number;
          this.summary = data.summary || {};
          // Rows start closed; keep open only the ones still on the page.
          const ids = new Set(this.items.map((r) => r.user_id));
          this.expanded = this.items.filter((r) =>
            this.expanded.some((open) => open.user_id === r.user_id && ids.has(r.user_id)));
        })
        .catch((err) => {
          if (ticket !== this.ticket) return;
          this.error = err;
          this.items = [];
          this.expanded = [];
          this.summary = {};
        })
        .finally(() => {
          if (ticket === this.ticket) this.loading = false;
        });
    },
    reload() {
      this.page = 1;
      this.load();
    },
    refresh() {
      this.refreshCount += 1;
      this.load();
    },
    onPage(page) {
      this.page = page;
      this.load();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    toggleRow(item, slotData) {
      if (slotData) slotData.expand(!slotData.isExpanded);
    },
    dayIst,
  },
};
</script>

<style scoped>
.kf-test {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--kf-fs-label);
  color: #1f2d36;
  background: #e8f1fb;
  border-radius: 6px;
  padding: 6px 10px;
  margin: 12px 0 8px;
}
.kf-nowrap {
  white-space: nowrap;
}
.kf-muted {
  color: #54636f;
}
.kf-user {
  min-width: 140px;
  line-height: 1.35;
}
.kf-line {
  min-width: 280px;
  max-width: 620px;
  line-height: 1.4;
  color: #1f2d36;
}
.kf-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 10px;
  min-width: 120px;
  font-size: var(--kf-fs-label);
  color: #54636f;
}
.kf-counts strong {
  color: #1f2d36;
}
.kf-detail {
  margin-top: 2px;
  font-size: var(--kf-fs-label);
  color: #54636f;
}
/* The rows open on click. */
::v-deep .kf-row {
  cursor: pointer;
}
/* The columns keep the header widths whatever is open: with the browser's
   automatic layout an open row's day list counted as content and resized
   them. Only the outer table; the day list has its own fixed layout. Phones
   stack the rows and have no columns to move. */
@media (min-width: 600px) {
  .kf-tracker ::v-deep .kf-table > .v-data-table__wrapper > table {
    table-layout: fixed;
  }
}
/* Phones: the table stacks each row as label / value, so the desktop column
   widths would push the values off screen. */
@media (max-width: 599px) {
  .kf-user,
  .kf-line,
  .kf-counts {
    min-width: 0;
    max-width: none;
  }
  /* Every table row is pinned to one height (index.css, !important); these
     stacked values wrap, so they need the more specific selector. */
  ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td.v-data-table__mobile-row {
    height: auto !important;
    min-height: 48px;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    gap: 12px;
  }
  ::v-deep .v-data-table__mobile-row__header {
    flex: 0 0 34%;
  }
  ::v-deep .v-data-table__mobile-row__cell {
    flex: 1 1 auto;
    min-width: 0;
    text-align: left;
  }
}
</style>
